import cn from 'classnames';
import { DragDropContext, type OnDragEndResponder } from 'react-beautiful-dnd';
import { useProvider } from 'utils/providers/Provider';
import { useTour } from '@reactour/tour';
import { TOUR_SELECTOR } from 'ui/partials/Tour';

import {
  useGetDealsBoard,
  useGetStages,
  useUpdateDealSequence,
} from '../hooks';
import { DroppableColumn, ColumnTotals } from '.';
import { useDealsContext } from '../containers/Deals/Deals';
import {
  deals as dealsMockData,
  stages as stagesMockData,
} from '../utils/tour-steps/how-to-manage-a-deal/mockData';

const DealsKanbanBoard = () => {
  const { state } = useProvider();
  const {
    pipelineId,
    query,
    setSelectedDealId,
    setSelectedDeals,
    selectedDeals,
  } = useDealsContext();
  const { data: stages } = useGetStages(pipelineId);
  const { data: deals } = useGetDealsBoard({ ...query, pipelineId });
  const { mutate: updateDealSequence } = useUpdateDealSequence();
  const { isOpen } = useTour();

  const onCheck = (id: string) => {
    setSelectedDeals?.((prevVal) => {
      if (prevVal.includes(id)) {
        return prevVal.filter((val) => val !== id);
      }

      return prevVal.concat(id);
    });
  };

  const onDragStart = () => {
    setSelectedDealId?.(null);
  };

  const onDragEnd: OnDragEndResponder = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    updateDealSequence({
      pipelineId,
      source,
      destination,
      draggableId,
      query: { ...query, pipelineId },
    });
  };

  return (
    <div
      className={cn(
        state?.isOpenBanner ? 'h-[calc(100vh-411px)]' : 'h-[calc(100vh-320px)]',
        !state?.collapse ? 'w-[calc(100vw-270px)]' : 'w-[calc(100vw-105px)]',
        'mt-[25px]',
      )}
    >
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <div
          data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.MANAGE.DEALS_BOARD}
          className={cn(
            'relative h-full overflow-x-auto',
            'space-x-4 whitespace-nowrap',
          )}
        >
          {(isOpen ? stagesMockData : stages)?.map((stage) => (
            <DroppableColumn
              selectedDeals={selectedDeals ?? []}
              onCheck={onCheck}
              id={stage._id}
              name={stage.name}
              key={stage._id}
              actions={stage.actions}
              items={
                isOpen
                  ? (dealsMockData.data as any)?.[stage._id]
                  : deals?.[stage._id]
              }
            />
          ))}
          <div className="sticky bottom-0 space-x-5">
            {(isOpen ? stagesMockData : stages)?.map((stage) => (
              <ColumnTotals
                items={
                  isOpen
                    ? (dealsMockData.data as any)?.[stage._id]
                    : deals?.[stage._id]
                }
                key={stage._id}
              />
            ))}
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};

export default DealsKanbanBoard;
